/**
 * /p/:slug — Productos individuales (minimalista)
 *
 * Productos creados desde "Producto individual" o quick-payment en el dashboard.
 * Página minimalista: nombre, precio, botón de pago. Opcionalmente imagen pequeña.
 *
 * No confundir con /gift/:slug: video, reel (ReelsToBuy), experiencia completa.
 * Si el producto tiene video/reel, usar /gift/:slug o /reel/:slug.
 */

import { useEffect, useState } from 'react';
import { getCanonicalLocation } from "@/lib/canonicalLocation";
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Loader2, ArrowLeft, Check } from 'lucide-react';
import MuxPlayer from '@mux/mux-player-react';
import { BRAND_PERSON_NAME } from "@/lib/brandConfig";

interface Product {
  id: string;
  name: string;
  slug: string | null;
  description: string | null;
  price_usd: number;
  recommended_price: number;
  recommended_currency: string;
  product_type: string;
  product_category: string | null;
  features?: string[] | null;
  image_url?: string | null;
  image_alt?: string | null;
  hero_image_url?: string | null;
  thumbnail_url?: string | null;
  gallery_images?: string[] | null;
  video_url?: string | null;
  cta_button_text?: string | null;
  cta_button_color?: string | null;
  show_quick_buy?: boolean | null;
  page_title?: string | null;
  page_description?: string | null;
}

function getApiBase(): string {
  if (typeof window === 'undefined') return '';
  return window.location.origin;
}

export default function ProductPageSimple() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);
  const [buyerEmail, setBuyerEmail] = useState("");

  useEffect(() => {
    if (!slug) return;

    const load = async () => {
      try {
        // VERDAD ÚNICA: país del Worker; fallback endpoint server (headers CF).
        let country = getCanonicalLocation().country || 'US';
        if (country === 'US' && !getCanonicalLocation().country) {
          try {
            const res = await fetch('/api/detect-country');
            const data = await res.json();
            if (data.country && data.country !== 'XX') country = data.country;
          } catch {
            /* ignore */
          }
        }

        const base = getApiBase();
        const res = await fetch(`${base}/api/products/by-slug?slug=${encodeURIComponent(slug)}&country=${country}`);
        if (!res.ok) {
          if (res.status === 404) throw new Error('Producto no encontrado');
          throw new Error('Error al cargar el producto');
        }
        const data = await res.json();
        const p = data.product;
        // Layout según doc: individual → /p/, otros → /gift/
        if (p?.product_category?.toLowerCase() !== 'individual') {
          navigate(`/gift/${slug}`, { replace: true });
          return;
        }
        setProduct(p);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [slug]);

  const handlePagar = async () => {
    if (!product?.slug) return;
    setPaying(true);
    const base = getApiBase();
    const currency = product.recommended_currency || 'USD';
    try {
      if (currency === 'ARS') {
        // ARS: MercadoPago create-preference
        const res = await fetch(`${base}/api/mercadopago/create-preference`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            productId: product.id,
            slug: product.slug,
            productName: product.name,
            price: product.recommended_price ?? product.price_ars ?? product.price_usd,
            currency: 'ARS',
            country: 'AR',
            payerEmail: buyerEmail || undefined,
            successPath: `/p/${product.slug}`,
            failurePath: `/p/${product.slug}`,
            pendingPath: `/p/${product.slug}`,
          }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data?.error || 'Error al crear preferencia');
        }
        const { init_point } = await res.json();
        if (init_point) window.location.href = init_point;
        else throw new Error('No se recibió el link de pago');
      } else {
        // USD: create-payment-intent → /pagar/{paymentIntentId}
        const res = await fetch(`${base}/api/products/create-payment-intent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ slug: product.slug, buyerEmail: buyerEmail || undefined }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data?.error || 'Error al iniciar el pago');
        }
        const { paymentIntentId } = await res.json();
        if (paymentIntentId) {
          window.location.href = `${base}/pagar/${paymentIntentId}`;
          return;
        }
        throw new Error('No se recibió el pago');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al pagar');
    } finally {
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <Loader2 className="h-8 w-8 animate-spin text-white/60" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black p-4">
        <div className="text-center max-w-sm">
          <p className="text-white/80 mb-4">{error || 'Producto no encontrado'}</p>
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            className="border-white/30 text-white hover:bg-white/10 hover:text-white"
          >
            Volver al inicio
          </Button>
        </div>
      </div>
    );
  }

  const description = product.page_description || product.description;
  const price = product.recommended_price;
  const currency = product.recommended_currency || 'USD';
  const buttonLabel = product.cta_button_text || 'Pagar';
  const buttonColor = product.cta_button_color || '#ffffff';
  const imageUrl = product.image_url || product.hero_image_url;
  const galleryImages = Array.isArray(product.gallery_images) ? product.gallery_images : [];
  const features = Array.isArray(product.features) ? product.features : [];

  useEffect(() => {
    const t = product.page_title || product.name;
    if (t) document.title = `${t} | ${BRAND_PERSON_NAME}`;
    const desc = product.page_description || product.description;
    if (desc) {
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute('content', String(desc));
    }
  }, [product.page_title, product.name, product.page_description, product.description]);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-white/10 bg-black/80 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver
          </Link>
          <Link to="/" className="font-semibold text-white">
            {BRAND_PERSON_NAME}
          </Link>
          <div className="w-16" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Video (Mux Playback ID) o imagen principal */}
        {product.video_url && (
          <div className="rounded-xl overflow-hidden mb-6 bg-black aspect-video">
            <MuxPlayer
              playbackId={product.video_url}
              streamType="on-demand"
              metadata={{ title: product.name }}
              className="w-full h-full object-cover"
              primaryColor="#ffffff"
              // Perf: video de producto sin autoplay — la mayoría de usuarios no le da play.
              // "metadata" baja solo manifest + segment0 (~30 KB) en vez del MP4 entero.
              // Cuando hace click, el player completa la descarga al toque.
              preload="metadata"
              playsInline
              maxResolution="1080p"
              capRenditionToPlayerSize
              disableCookies
            />
          </div>
        )}
        {!product.video_url && imageUrl && (
          <div className="rounded-xl overflow-hidden mb-6 bg-white/5 aspect-video max-h-64">
            <img
              src={imageUrl}
              alt={product.image_alt || product.name}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        {/* Galería adicional (thumbnails) */}
        {galleryImages.length > 0 && (
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {galleryImages.slice(0, 6).map((url, i) => (
              <div key={i} className="shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-white/5">
                <img src={url} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        )}

        {/* Contenido */}
        <article className="space-y-6">
          <h1 className="text-2xl md:text-3xl font-semibold text-white">
            {product.name}
          </h1>

          {description && (
            <p className="text-white/70 whitespace-pre-wrap leading-relaxed">
              {description}
            </p>
          )}

          {features.length > 0 && (
            <ul className="space-y-2">
              {features.map((f, i) => (
                <li key={i} className="flex items-start gap-3 text-white/80">
                  <Check className="h-5 w-5 shrink-0 text-emerald-400 mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Precio */}
          <div className="flex items-baseline gap-2 mb-6">
            <span className="text-3xl font-bold text-white">
              {currency} {price}
            </span>
          </div>

          {/* Email para recibo y recovery (capturado temprano) */}
          <div className="mb-4">
            <input
              type="email"
              placeholder="tu@email.com (para recibo y recuperación)"
              value={buyerEmail}
              onChange={(e) => setBuyerEmail(e.target.value)}
              className="w-full rounded-md bg-white/10 border border-white/20 px-4 py-2 text-white placeholder:text-white/50"
            />
          </div>

          {/* CTA */}
          <Button
            size="lg"
            className="w-full font-semibold text-base py-6"
            style={{
              backgroundColor: buttonColor,
              color: isLightColor(buttonColor) ? '#000' : '#fff',
            }}
            onClick={handlePagar}
            disabled={paying}
          >
            {paying ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Preparando pago...
              </>
            ) : (
              buttonLabel
            )}
          </Button>

          {product.show_quick_buy && product.slug && (
            <Button
              variant="outline"
              size="lg"
              className="w-full mt-3 border-white/30 text-white/80 hover:bg-white/10 hover:text-white"
              onClick={() => navigate(`/buy/${product.slug}`)}
            >
              Compra Rápida (1 Click)
            </Button>
          )}
        </article>
      </main>
    </div>
  );
}

function isLightColor(hex: string): boolean {
  const h = hex.replace('#', '');
  let r = 0, g = 0, b = 0;
  if (h.length === 6) {
    r = parseInt(h.slice(0, 2), 16) || 0;
    g = parseInt(h.slice(2, 4), 16) || 0;
    b = parseInt(h.slice(4, 6), 16) || 0;
  } else if (h.length === 3) {
    r = parseInt(h[0] + h[0], 16) || 0;
    g = parseInt(h[1] + h[1], 16) || 0;
    b = parseInt(h[2] + h[2], 16) || 0;
  } else return false;
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6;
}
